import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DAYS_OF_WEEK } from "@shared/types/foyer";
import type {
  DayOfWeek,
  OpeningHours,
  OpeningHoursDay,
  PremiseAudience,
} from "@shared/types/foyer";
import { weeklyOpenMinutes } from "@shared/opening-hours";

/**
 * When this place is open, and roughly how many people come through it.
 *
 * ## Where this lives, and where it deliberately does not
 *
 * **Location settings, not the Dashboard.** A premise owner signs in and lands
 * on the dashboard: it is a read-at-a-glance surface, and a form somebody
 * fills in once at setup both hides in it and clutters it. These belong with
 * the other things about a place that are configured and then left alone.
 *
 * The times are read in the location's own timezone, which is why the page
 * says so next to them - "09:00" is not a time until you know where.
 *
 * The two footfall figures sit here rather than anywhere else for the reason
 * they exist at all: an advertising impression is priced from visitors over
 * opening hours, so splitting them across two surfaces would let somebody fill
 * in half the sum and wonder why nothing earns.
 */

const LABELS: Record<DayOfWeek, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export interface OpeningHoursFieldsProps {
  hours: OpeningHours | undefined;
  audience: PremiseAudience | undefined;
  onChange: (next: {
    openingHours?: OpeningHours;
    audience?: PremiseAudience;
  }) => void;
}

export default function OpeningHoursFields({
  hours,
  audience,
  onChange,
}: OpeningHoursFieldsProps) {
  const patchDay = (day: DayOfWeek, next: OpeningHoursDay | undefined) => {
    const merged: OpeningHours = { ...(hours ?? {}) };
    // Deleting rather than storing a `closed: true` row: a day absent from the
    // record already MEANS closed (see `isOpenOn`), and two ways of saying the
    // same thing is how the two drift apart.
    if (!next) delete merged[day];
    else merged[day] = next;
    onChange({
      openingHours: Object.keys(merged).length > 0 ? merged : undefined,
    });
  };

  const patchAudience = (next: Partial<PremiseAudience>) => {
    const merged = { ...(audience ?? {}), ...next };
    const empty =
      merged.entriesPerWeek === undefined && merged.dwellMinutes === undefined;
    onChange({ audience: empty ? undefined : merged });
  };

  const weeklyHours = Math.round((weeklyOpenMinutes(hours) / 60) * 10) / 10;

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{ mt: 1, mb: 1, color: "text.secondary" }}
      >
        Opening hours
      </Typography>

      <Stack spacing={0.75}>
        {DAYS_OF_WEEK.map((day) => {
          const d = hours?.[day];
          const open = Boolean(d);
          const badOpens = Boolean(d && !d.allDay && !HHMM.test(d.opens));
          const badCloses = Boolean(d && !d.allDay && !HHMM.test(d.closes));
          return (
            <Stack
              key={day}
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid={`hours-${day}`}
            >
              <FormControlLabel
                sx={{ width: 132, mr: 0, flexShrink: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={open}
                    inputProps={{ "aria-label": LABELS[day] }}
                    onChange={(e) =>
                      patchDay(
                        day,
                        e.target.checked
                          ? { opens: "09:00", closes: "17:00" }
                          : undefined
                      )
                    }
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13 }}>{LABELS[day]}</Typography>
                }
              />
              {open && !d!.allDay && (
                <>
                  <TextField
                    size="small"
                    value={d!.opens}
                    error={badOpens}
                    onChange={(e) =>
                      patchDay(day, { ...d!, opens: e.target.value })
                    }
                    inputProps={{
                      "aria-label": `${LABELS[day]} opens`,
                      placeholder: "09:00",
                    }}
                    sx={{ width: 88 }}
                  />
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    to
                  </Typography>
                  <TextField
                    size="small"
                    value={d!.closes}
                    error={badCloses}
                    onChange={(e) =>
                      patchDay(day, { ...d!, closes: e.target.value })
                    }
                    inputProps={{
                      "aria-label": `${LABELS[day]} closes`,
                      placeholder: "17:00",
                    }}
                    sx={{ width: 88 }}
                  />
                </>
              )}
              {open && (
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(d!.allDay)}
                      inputProps={{ "aria-label": `${LABELS[day]} all day` }}
                      onChange={(e) =>
                        patchDay(day, { ...d!, allDay: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13 }}>24 hours</Typography>
                  }
                />
              )}
            </Stack>
          );
        })}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {weeklyOpenMinutes(hours) > 0
          ? `Open ${weeklyHours} hours a week. Unticked days are closed.`
          : "Unticked days are closed. A closing time earlier than the opening time runs past midnight."}
      </Typography>

      <Typography
        variant="subtitle2"
        sx={{ mt: 2.5, mb: 1, color: "text.secondary" }}
      >
        Footfall
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          label="Visitors per week"
          type="number"
          size="small"
          sx={{ flex: 1 }}
          value={audience?.entriesPerWeek ?? ""}
          placeholder="Not set"
          inputProps={{ min: 0, "aria-label": "Visitors per week" }}
          onChange={(e) =>
            patchAudience({
              entriesPerWeek: numberOrUndefined(e.target.value),
            })
          }
        />
        <TextField
          label="Average visit (minutes)"
          type="number"
          size="small"
          sx={{ flex: 1 }}
          value={audience?.dwellMinutes ?? ""}
          placeholder="Not set"
          inputProps={{ min: 0, "aria-label": "Average visit" }}
          onChange={(e) =>
            patchAudience({ dwellMinutes: numberOrUndefined(e.target.value) })
          }
        />
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        An estimate, not a measurement. Advertising prices an impression from
        these and the opening hours above, so a screen here earns nothing until
        all three are filled in.
      </Typography>
    </Box>
  );
}

/** A blank field is a RETRACTION, not a zero - "not stated" has to be sayable. */
function numberOrUndefined(raw: string): number | undefined {
  if (raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
}
